'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  toggleReaction,
  getReactionCounts,
  getUserReactions,
  REACTION_META,
  type ReactionType,
  type ContentType,
  type ReactionCounts,
  type UserReactions,
} from '@/lib/db/reactions';

interface ReactionButtonsProps {
  contentType: ContentType;
  contentId: string;
  /** Compact mode: smaller buttons for cards */
  compact?: boolean;
  /** Initial counts (optional, avoids extra fetch if already known) */
  initialCounts?: ReactionCounts;
}

const REACTION_TYPES: ReactionType[] = ['insightful', 'needs_citation', 'eye_opening'];

export default function ReactionButtons({
  contentType,
  contentId,
  compact = false,
  initialCounts,
}: ReactionButtonsProps) {
  const { user } = useAuth();
  const [counts, setCounts] = useState<ReactionCounts>(
    initialCounts || { insightful: 0, needs_citation: 0, eye_opening: 0 }
  );
  const [userReactions, setUserReactions] = useState<UserReactions>({
    insightful: false,
    needs_citation: false,
    eye_opening: false,
  });
  const [loading, setLoading] = useState(!initialCounts);
  const [toggling, setToggling] = useState<ReactionType | null>(null);

  const fetchData = useCallback(async () => {
    const [countsData, userReactionsData] = await Promise.all([
      getReactionCounts(contentType, contentId),
      user ? getUserReactions(contentType, contentId, user.id) : Promise.resolve({ insightful: false, needs_citation: false, eye_opening: false }),
    ]);
    setCounts(countsData);
    setUserReactions(userReactionsData);
    setLoading(false);
  }, [contentType, contentId, user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Real-time subscription
  useEffect(() => {
    const supabase = createClient();
    const channel = supabase
      .channel(`reactions:${contentType}:${contentId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'reactions',
          filter: `content_id=eq.${contentId}`,
        },
        () => {
          // Refetch counts on any change
          getReactionCounts(contentType, contentId).then(setCounts);
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [contentType, contentId]);

  const handleToggle = async (reactionType: ReactionType) => {
    if (!user || toggling) return;
    setToggling(reactionType);

    // Optimistic update
    const wasActive = userReactions[reactionType];
    setUserReactions(prev => ({ ...prev, [reactionType]: !wasActive }));
    setCounts(prev => ({
      ...prev,
      [reactionType]: wasActive ? Math.max(0, prev[reactionType] - 1) : prev[reactionType] + 1,
    }));

    await toggleReaction(contentType, contentId, user.id, reactionType);
    setToggling(null);
  };

  if (loading) {
    return (
      <div className={`flex items-center gap-1.5 ${compact ? '' : 'flex-wrap'}`}>
        {REACTION_TYPES.map(rt => (
          <div
            key={rt}
            className={`rounded-full animate-pulse ${compact ? 'h-6 w-16' : 'h-8 w-24'}`}
            style={{ backgroundColor: 'var(--muted)' }}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-1.5 ${compact ? '' : 'flex-wrap'}`}>
      {REACTION_TYPES.map(rt => {
        const meta = REACTION_META[rt];
        const active = userReactions[rt];
        const count = counts[rt];
        const isToggling = toggling === rt;

        return (
          <button
            key={rt}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleToggle(rt); }}
            disabled={!user || isToggling}
            title={user ? (active ? `Remove "${meta.label}" reaction` : `React: ${meta.label}`) : `Sign in to react`}
            className={`
              inline-flex items-center gap-1 rounded-full border transition-all duration-150
              ${compact ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-xs'}
              ${!user ? 'cursor-default opacity-70' : 'cursor-pointer hover:opacity-90'}
              ${isToggling ? 'opacity-60' : ''}
            `}
            style={{
              backgroundColor: active ? meta.bg : 'transparent',
              borderColor: active ? meta.color : 'var(--border)',
              color: active ? meta.color : 'var(--muted-foreground)',
            }}
          >
            <span className={compact ? 'text-xs' : 'text-sm'}>{meta.emoji}</span>
            {!compact && (
              <span className="font-medium" style={{ color: active ? meta.color : 'var(--muted-foreground)' }}>
                {meta.label}
              </span>
            )}
            {count > 0 && (
              <span
                className="font-semibold tabular-nums"
                style={{ color: active ? meta.color : 'var(--muted-foreground)' }}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
